import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState } from "react";
import {
  HistoryAddedBlockDTO,
  HistoryCloserDetailDTO,
} from "../../HubSpot/useGetHubSpotCloserAvailabilityHistory.ts";
import {
  dayLabel,
  nameFromEmail,
  sumTotals,
  sumTransitions,
} from "./closerAvailabilityHistoryStats.ts";
import { CloserSlotGrid } from "./CloserSlotGrid.tsx";
import { tdClass, thClass, thLeftClass, tdLeftClass } from "./HistoryCells.tsx";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Europe/Rome";

type Props = {
  closers: HistoryCloserDetailDTO[];
  addedBlocks: HistoryAddedBlockDTO[];
  comparable: boolean;
};

function FlowCard({
  swatch,
  label,
  value,
  note,
}: {
  swatch: string;
  label: string;
  value: string | number;
  note: string;
}) {
  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        <span className={`inline-block w-3 h-3 rounded-sm ${swatch}`} />
        {label}
      </span>
      <span className="text-xl font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-gray-400">{note}</span>
    </div>
  );
}

export function HistoryChangesPanel({
  closers,
  addedBlocks,
  comparable,
}: Props) {
  const emails = new Set(closers.map((c) => c.email));
  const blocks = addedBlocks.filter((b) => emails.has(b.email));
  const t = sumTransitions(closers);
  const totals = sumTotals(closers);

  const worstCloser = [...closers]
    .filter((c) => c.closure)
    .sort(
      (a, b) =>
        (a.closure?.effective ?? 0) -
        (a.opening?.effective ?? 0) -
        ((b.closure?.effective ?? 0) - (b.opening?.effective ?? 0)),
    )[0];
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const gridCloser =
    closers.find((c) => c.email === selectedEmail) ?? worstCloser ?? closers[0];

  const time = (d: string) => dayjs(d).tz(TZ).format("HH:mm");

  return (
    <div className="space-y-5">
      {!comparable && (
        <p className="text-sm text-gray-500">
          Per questo periodo manca l'apertura o la chiusura: non c'è niente con
          cui confrontare, quindi i cambiamenti non sono disponibili.
        </p>
      )}
      <div className="grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(170px,1fr))]">
        <FlowCard
          swatch="bg-blue-500"
          label="Libero → call"
          value={t.booked}
          note="prenotazioni"
        />
        <FlowCard
          swatch="bg-red-500"
          label="Libero → OOO"
          value={t.blocked}
          note="disponibilità tolta"
        />
        <FlowCard
          swatch="bg-red-800"
          label="Call → OOO"
          value={t.callBlocked}
          note="call saltata e slot bloccato"
        />
        <FlowCard
          swatch="bg-white ring-2 ring-inset ring-green-600"
          label="OOO → libero/call"
          value={t.freed}
          note="disponibilità aggiunta"
        />
        <FlowCard
          swatch="bg-white ring-2 ring-inset ring-blue-500"
          label="Closer entrati / usciti"
          value={comparable ? `${totals.joined} / ${totals.left}` : "—"}
          note="rispetto all'apertura"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2 items-start">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="font-semibold">Blocchi aggiunti dopo l'apertura</h2>
            <span className="text-xs text-gray-400">
              {blocks.length} blocchi ·{" "}
              {blocks.reduce((acc, b) => acc + b.slots, 0)} slot
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse">
              <thead>
                <tr>
                  <th className={thLeftClass}>Closer</th>
                  <th className={thLeftClass}>Quando</th>
                  <th className={thLeftClass}>Evento</th>
                  <th className={thClass}>Slot</th>
                </tr>
              </thead>
              <tbody>
                {blocks.length === 0 && (
                  <tr>
                    <td colSpan={4} className={`${tdLeftClass} text-gray-400`}>
                      Nessun blocco aggiunto.
                    </td>
                  </tr>
                )}
                {blocks.map((b) => (
                  <tr key={`${b.email}-${b.slotStart}`}>
                    <td className={tdLeftClass}>{nameFromEmail(b.email)}</td>
                    <td className={tdLeftClass}>
                      {dayLabel(b.date)}, {time(b.slotStart)}–{time(b.slotEnd)}
                    </td>
                    <td className={`${tdLeftClass} whitespace-normal`}>
                      {b.eventTitle ?? "(senza titolo)"}
                      {b.calls > 0 && (
                        <span className="ml-2 text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 bg-red-100 text-red-800">
                          {b.calls} call
                        </span>
                      )}
                    </td>
                    <td className={`${tdClass} text-red-600`}>−{b.slots}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-end justify-between gap-2">
            <h2 className="font-semibold">Agenda slot per slot</h2>
            <select
              value={gridCloser?.email ?? ""}
              onChange={(e) => setSelectedEmail(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm"
            >
              {closers.map((c) => (
                <option key={c.email} value={c.email}>
                  {nameFromEmail(c.email)}
                </option>
              ))}
            </select>
          </div>
          {gridCloser?.slots && <CloserSlotGrid slots={gridCloser.slots} />}
        </div>
      </div>
    </div>
  );
}
